"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useSession } from "./SessionContext";

interface TourButtonContextType {
  showTourButtons: boolean;
  setShowTourButtons: (show: boolean) => void;
}

const TourButtonContext = createContext<TourButtonContextType | undefined>(
  undefined
);

export const TourButtonProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { session } = useSession();
  const [showTourButtons, setShowTourButtons] = useState(true);

  // Load preference from session (which comes from database)
  useEffect(() => {
    if (session.showTourButtons !== undefined) {
      setShowTourButtons(session.showTourButtons);
      // Also sync to localStorage as cache
      localStorage.setItem("show-tour-buttons", JSON.stringify(session.showTourButtons));
    } else {
      // Fallback to localStorage if session not loaded yet
      const savedPreference = localStorage.getItem("show-tour-buttons");
      if (savedPreference !== null) {
        setShowTourButtons(JSON.parse(savedPreference));
      }
    }
  }, [session.showTourButtons]);

  // Listen for custom events from settings
  useEffect(() => {
    const handleTourButtonsToggle = (event: CustomEvent) => {
      setShowTourButtons(event.detail.enabled);
    };

    window.addEventListener(
      "tour-buttons-toggle",
      handleTourButtonsToggle as EventListener
    );

    return () => {
      window.removeEventListener(
        "tour-buttons-toggle",
        handleTourButtonsToggle as EventListener
      );
    };
  }, []);

  const value: TourButtonContextType = {
    showTourButtons,
    setShowTourButtons,
  };

  return (
    <TourButtonContext.Provider value={value}>
      {children}
    </TourButtonContext.Provider>
  );
};

export const useTourButtons = () => {
  const context = useContext(TourButtonContext);
  if (context === undefined) {
    throw new Error("useTourButtons must be used within a TourButtonProvider");
  }
  return context;
};
